/*
 * The zone list comes from the browser's own tzdata, the same data that formats
 * every date here; a bundled list would be a second copy that goes stale.
 */

interface TimeZoneOption {
  value: string;
  label: string;
}

/*
 * Chrome omits the `Etc/*` zones and `UTC` from its supported list, unlike
 * Firefox and Safari. UTC is the one people actually reach for, so it is added
 * back when missing rather than being a browser-dependent gap in the picker.
 */
function canonicalZones(): string[] {
  const zones = Intl.supportedValuesOf('timeZone');
  return zones.includes('UTC') ? zones : ['UTC', ...zones];
}

/** `+02:00`, or an empty string if the engine will not name the offset. */
function offsetOf(timeZone: string, now: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(now);

    return (
      parts.find((part) => part.type === 'timeZoneName')?.value.replace('GMT', '') ?? ''
    );
  } catch {
    /* An engine that cannot format a zone it listed — show the name alone. */
    return '';
  }
}

/**
 * Every zone labelled with its offset at `now`, which daylight saving shifts.
 * `selected` is appended when unlisted, so a native select never silently
 * swaps a stored zone for its first entry.
 */
export function timeZoneOptions(
  selected: string | undefined,
  now: Date,
): TimeZoneOption[] {
  const zones = canonicalZones();
  const all = selected && !zones.includes(selected) ? [...zones, selected] : zones;

  return all
    .map((value) => {
      const offset = offsetOf(value, now);
      return {
        value,
        label: offset
          ? `${value.replace(/_/g, ' ')} (UTC${offset})`
          : value.replace(/_/g, ' '),
      };
    })
    .sort((a, b) => a.value.localeCompare(b.value));
}
